import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { adminApi } from '../../../utils/api';
import { Spinner, Card, InsightCard } from '../shared';

const fmtCost = (n) => {
    if (n == null) return '$0.0000';
    return `$${n.toFixed(4)}`;
};

const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    });
};

const fmtPercent = (n) => {
    if (n == null) return '0%';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
};

function KeyMetricsCard({ label, value, trend, sublabel }) {
    const getTrendColor = (direction) => {
        if (direction === 'up') return 'text-green-600';
        if (direction === 'down') return 'text-red-600';
        return 'text-gray-500';
    };

    const getTrendIcon = (direction) => {
        if (direction === 'up') return '↑';
        if (direction === 'down') return '↓';
        return '→';
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-sm font-semibold text-gray-900 mt-1 break-all">{value}</p>
            {trend && (
                <p className={`text-xs mt-1 ${getTrendColor(trend.direction)}`}>
                    {getTrendIcon(trend.direction)} {fmtPercent(trend.change_percent)}
                </p>
            )}
            {sublabel && !trend && (
                <p className="text-xs text-gray-500 mt-1">{sublabel}</p>
            )}
        </div>
    );
}

function ModelBreakdownChart({ modelBreakdown }) {
    if (!modelBreakdown || modelBreakdown.length === 0) {
        return <p className="text-sm text-gray-400 text-center py-4">No model usage data</p>;
    }

    const maxCost = Math.max(...modelBreakdown.map(m => m.total_cost));

    return (
        <div className="space-y-3">
            {modelBreakdown.map((model, idx) => (
                <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-gray-700">{model.model_name}</span>
                        <div className="flex items-center gap-3">
                            <span className="text-gray-500">{model.request_count} reqs</span>
                            <span className="font-mono text-gray-700">{fmtCost(model.total_cost)}</span>
                            <span className="text-gray-500 w-12 text-right">{model.percentage.toFixed(1)}%</span>
                        </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-x-auto">
                        <div
                            className="bg-blue-500 h-full transition-all"
                            style={{
                                width: `${maxCost > 0 ? (model.total_cost / maxCost) * 100 : 0}%`,
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

function HealthScoreCard({ healthScore }) {
    if (!healthScore) return null;

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-green-600';
        if (score >= 60) return 'text-blue-600';
        if (score >= 40) return 'text-amber-600';
        return 'text-red-600';
    };

    const getScoreBg = (score) => {
        if (score >= 80) return 'bg-green-100';
        if (score >= 60) return 'bg-blue-100';
        if (score >= 40) return 'bg-amber-100';
        return 'bg-red-100';
    };

    const factorTooltips = {
        activity: { title: 'Mức độ hoạt động', desc: 'Đo lường mức độ user sử dụng platform.', levels: ['Tốt (80+): >80 requests/tháng', 'TB (40-80): 40-80 requests/tháng', 'Kém (<40): <40 requests/tháng'] },
        cost_efficiency: { title: 'Hiệu quả chi phí', desc: 'So sánh hiệu quả chi phí với trung bình platform.', levels: ['Tốt (100): ≤ trung bình platform', 'TB (75): ≤ 1.5x trung bình', 'Kém (50): > 1.5x trung bình'] },
        engagement: { title: 'Mức độ tương tác', desc: 'Đo lường sự tương tác qua số session tạo.', levels: ['Tốt (80+): >8 sessions', 'TB (40-80): 4-8 sessions', 'Kém (<40): <4 sessions'] },
        storage: { title: 'Tình trạng lưu trữ', desc: 'Dung lượng sử dụng so với giới hạn tier.', levels: ['Tốt (80+): <20% đã dùng', 'TB (40-80): 20-60% đã dùng', 'Kém (<40): >60% đã dùng'] },
        recency: { title: 'Hoạt động gần đây', desc: 'Đo lường thời gian user hoạt động lần cuối.', levels: ['Tốt (100): Trong 7 ngày', 'TB (50-75): Trong 7-30 ngày', 'Kém (0): >30 ngày'] },
    };

    const getFactorStatus = (key, score) => {
        if (score >= 80) return '✓';
        if (score >= 40) return '⚠';
        return '✗';
    };

    const getFactorStatusColor = (score) => {
        if (score >= 80) return 'text-green-600';
        if (score >= 40) return 'text-amber-600';
        return 'text-red-600';
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4 overflow-visible">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-700">Health Score</h3>
                    <div className="relative group">
                        <div className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center cursor-help">
                            <span className="text-xs text-gray-500">?</span>
                        </div>
                        <div className="absolute left-0 top-6 z-50 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg leading-relaxed">
                            <p className="font-semibold mb-1">Điểm sức khỏe tổng thể</p>
                            <p className="mb-1">5 yếu tố có trọng số:</p>
                            <ul className="space-y-0.5 mb-1.5">
                                <li>Hoạt động (30%): Số lượng requests</li>
                                <li>Hiệu quả chi phí (20%): Chi phí so với trung bình</li>
                                <li>Tương tác (25%): Số session tạo</li>
                                <li>Lưu trữ (15%): Dung lượng sử dụng</li>
                                <li>Gần đây (10%): Thời gian hoạt động cuối</li>
                            </ul>
                            <p>80+ Xuất sắc, 60-80 Tốt, 40-60 Trung bình, &lt;40 Kém</p>
                        </div>
                    </div>
                </div>
                <div className={`rounded-full px-3 py-1 text-xs font-semibold ${getScoreBg(healthScore.score)} ${getScoreColor(healthScore.score)}`}>
                    {healthScore.status.toUpperCase()}
                </div>
            </div>
            <div className="flex items-center gap-4">
                <div className={`text-4xl font-bold ${getScoreColor(healthScore.score)}`}>
                    {healthScore.score}
                </div>
                <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-x-auto">
                        <div
                            className={`h-full transition-all ${healthScore.score >= 80 ? 'bg-green-500' : healthScore.score >= 60 ? 'bg-blue-500' : healthScore.score >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${healthScore.score}%` }}
                        />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        {Object.entries(healthScore.factors).map(([key, factor], idx) => {
                            const tip = factorTooltips[key];
                            const isRight = idx % 2 === 1;
                            return (
                                <div key={key} className="relative group flex items-center justify-between cursor-help">
                                    <div className="flex items-center gap-1">
                                        <span className={getFactorStatusColor(factor.score)}>
                                            {getFactorStatus(key, factor.score)}
                                        </span>
                                        <span className="text-gray-500 capitalize">{key.replace('_', ' ')}</span>
                                    </div>
                                    <span className={`font-medium ${getFactorStatusColor(factor.score)}`}>
                                        {Math.round(factor.score)}
                                    </span>
                                    {tip && (
                                        <div className={`absolute top-full mt-1.5 z-50 hidden group-hover:block w-56 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg leading-relaxed ${isRight ? 'right-0' : 'left-0'}`}>
                                            <p className="font-semibold mb-1">{tip.title} ({Math.round(factor.score)}/100)</p>
                                            <ul className="space-y-0.5 mb-1">
                                                {tip.levels.map((l, i) => <li key={i}>{['✓','⚠','✗'][i]} {l}</li>)}
                                            </ul>
                                            <p className="text-gray-400">{tip.desc}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PlatformComparisonCard({ comparisons }) {
    if (!comparisons || comparisons.length === 0) return null;

    const getStatusColor = (status) => {
        if (status === 'normal') return 'text-gray-600';
        if (status === 'above') return 'text-red-600';
        return 'text-green-600';
    };

    const getStatusIcon = (status) => {
        if (status === 'normal') return '→';
        if (status === 'above') return '↑';
        return '↓';
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Platform Comparison</h3>
            <div className="space-y-3">
                {comparisons.map((comp, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                        <div className="flex-1">
                            <p className="text-xs text-gray-500">{comp.metric}</p>
                            <p className="text-sm font-medium text-gray-900">{comp.user_value.toFixed(4)}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-gray-500">Platform Avg</p>
                            <p className="text-sm text-gray-700">{comp.platform_avg.toFixed(4)}</p>
                        </div>
                        <div className={`ml-3 text-sm font-semibold ${getStatusColor(comp.status)}`}>
                            {getStatusIcon(comp.status)} {fmtPercent(comp.difference_percent)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PeakActivityCard({ activityPattern }) {
    if (!activityPattern) return null;

    const formatHour = (hour) => {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}${period}`;
    };

    const topHours = activityPattern.hourly_distribution
        .sort((a, b) => b.request_count - a.request_count)
        .slice(0, 5);

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Peak Activity Hours</h3>
            <div className="space-y-2">
                {topHours.map((hour) => (
                    <div key={hour.hour} className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-700 w-12">{formatHour(hour.hour)}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-x-auto">
                            <div
                                className="bg-blue-500 h-full transition-all"
                                style={{ width: `${hour.percentage}%` }}
                            />
                        </div>
                        <span className="text-xs text-gray-500 w-16 text-right">{hour.request_count} reqs</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StorageUsageCard({ resourceUsage }) {
    if (!resourceUsage) return null;

    const getStatusColor = (status) => {
        if (status === 'critical') return 'text-red-600';
        if (status === 'warning') return 'text-amber-600';
        return 'text-green-600';
    };

    const getStatusBg = (status) => {
        if (status === 'critical') return 'bg-red-100';
        if (status === 'warning') return 'bg-amber-100';
        return 'bg-green-100';
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Storage Usage</h3>
                <div className={`rounded-full px-2 py-0.5 text-xs font-semibold ${getStatusBg(resourceUsage.status)} ${getStatusColor(resourceUsage.status)}`}>
                    {resourceUsage.status.toUpperCase()}
                </div>
            </div>
            <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{resourceUsage.storage_used_mb.toFixed(1)} MB</span>
                    <span className="text-gray-500">/ {resourceUsage.storage_limit_mb} MB</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-x-auto">
                    <div
                        className={`h-full transition-all ${resourceUsage.status === 'critical' ? 'bg-red-500' : resourceUsage.status === 'warning' ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(resourceUsage.usage_percent, 100)}%` }}
                    />
                </div>
                <p className="text-xs text-gray-500 text-center">{resourceUsage.usage_percent.toFixed(1)}% used</p>
            </div>
        </div>
    );
}

function SevenDaySparkline({ sevenDayTrend }) {
    if (!sevenDayTrend || sevenDayTrend.values.length === 0) return null;

    const maxValue = Math.max(...sevenDayTrend.values);
    const barHeight = (value) => maxValue > 0 ? Math.max((value / maxValue) * 100, value > 0 ? 4 : 0) : 0;

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">7-Day Cost Trend</h3>
            <div className="flex items-end gap-1.5" style={{ height: 64 }}>
                {sevenDayTrend.values.map((value, idx) => (
                    <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full">
                        <div
                            className="w-full bg-blue-500 rounded-t transition-all min-w-[4px]"
                            style={{ height: `${barHeight(value)}%` }}
                            title={`${sevenDayTrend.dates[idx]}: $${value.toFixed(4)}`}
                        />
                    </div>
                ))}
            </div>
            <div className="flex gap-1.5 mt-1">
                {sevenDayTrend.dates.map((date, idx) => (
                    <span key={idx} className="flex-1 text-center text-[10px] text-gray-400">{date}</span>
                ))}
            </div>
        </div>
    );
}

function ActivityTrendChart({ activityTrend }) {
    const chartRef = useRef(null);
    const [viewMode, setViewMode] = useState('chart');

    if (!activityTrend || activityTrend.length === 0) {
        return <p className="text-sm text-gray-400 text-center py-4">No activity data</p>;
    }

    const categories = activityTrend.map((day) => {
        const parts = day.date.split('-');
        return `${parts[1]}-${parts[2]}`;
    });

    const requestsData = activityTrend.map(day => day.request_count);
    const costData = activityTrend.map(day => day.cost);
    const maxRequests = Math.max(...requestsData);
    const maxCost = Math.max(...costData);

    const chartOptions = {
        chart: {
            height: 260,
            style: { fontFamily: 'inherit' },
            spacingTop: 10,
        },
        title: { text: null },
        credits: { enabled: false },
        xAxis: {
            categories,
            crosshair: { width: 1, color: '#d1d5db', dashStyle: 'Dash' },
            labels: {
                style: { fontSize: '10px', color: '#9ca3af' },
                rotation: activityTrend.length > 20 ? -45 : 0,
            },
            lineColor: '#e5e7eb',
            tickLength: 0,
        },
        yAxis: [
            {
                title: {
                    text: 'Requests',
                    style: { fontSize: '10px', color: '#3b82f6', fontWeight: '500' },
                },
                labels: {
                    style: { fontSize: '10px', color: '#3b82f6' },
                    format: '{value}',
                },
                gridLineColor: '#f3f4f6',
                min: 0,
            },
            {
                title: {
                    text: 'Cost ($)',
                    style: { fontSize: '10px', color: '#f59e0b', fontWeight: '500' },
                },
                labels: {
                    style: { fontSize: '10px', color: '#f59e0b' },
                    format: '${value}',
                },
                opposite: true,
                gridLineWidth: 0,
                min: 0,
            },
        ],
        tooltip: {
            shared: true,
            style: { fontSize: '11px' },
        },
        plotOptions: {
            area: {
                fillOpacity: 0.08,
                lineWidth: 2,
                marker: {
                    enabled: false,
                    symbol: 'circle',
                    radius: 3,
                    states: { hover: { enabled: true } },
                },
            },
        },
        legend: {
            align: 'left',
            verticalAlign: 'bottom',
            symbolRadius: 2,
            symbolHeight: 8,
            symbolWidth: 12,
            itemStyle: { fontSize: '10px', color: '#6b7280', fontWeight: '400' },
        },
        series: [
            {
                name: 'Requests',
                type: 'area',
                yAxis: 0,
                data: requestsData,
                color: '#3b82f6',
                tooltip: {
                    valueSuffix: ' requests',
                },
            },
            {
                name: 'Cost',
                type: 'area',
                yAxis: 1,
                data: costData,
                color: '#f59e0b',
                tooltip: {
                    valuePrefix: '$',
                    valueDecimals: 4,
                },
            },
        ],
    };

    return (
        <div>
            <div className="flex items-center justify-end mb-4">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setViewMode('chart')}
                        className={`h-7 px-2.5 text-xs rounded-md border transition-colors ${
                            viewMode === 'chart'
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        Chart
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode('table')}
                        className={`h-7 px-2.5 text-xs rounded-md border transition-colors ${
                            viewMode === 'table'
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        Table
                    </button>
                </div>
            </div>

            {viewMode === 'chart' ? (
                <div className="px-2">
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                </div>
            ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {activityTrend.map((day, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-xs">
                            <span className="w-20 text-gray-500">{fmtDate(day.date)}</span>
                            <div className="flex-1 flex gap-1">
                                <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden" title="Requests">
                                    <div
                                        className="bg-blue-500 h-full transition-all"
                                        style={{
                                            width: `${maxRequests > 0 ? Math.min((day.request_count / maxRequests) * 100, 100) : 0}%`,
                                        }}
                                    />
                                </div>
                                <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden" title="Cost">
                                    <div
                                        className="bg-amber-500 h-full transition-all"
                                        style={{
                                            width: `${maxCost > 0 ? Math.min((day.cost / maxCost) * 100, 100) : 0}%`,
                                        }}
                                    />
                                </div>
                            </div>
                            <span className="w-16 text-right text-gray-700">{day.request_count} reqs</span>
                            <span className="w-20 text-right font-mono text-gray-700">{fmtCost(day.cost)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function UsageHistoryPopup({ userId, limitMode, limits, onClose }) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi.getUserUsageHistory(userId, 20)
            .then((res) => setRecords(res.records || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [userId]);

    const fmtWindow = (iso) => {
        if (!iso) return '-';
        const d = new Date(iso);
        return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const getUtilization = (record) => {
        if (!limits || !limitMode || limitMode === 'disabled') return 0;
        if (limitMode === 'requests' && limits.request_limit > 0)
            return Math.min(100, (record.request_count / limits.request_limit) * 100);
        if (limitMode === 'tokens' && limits.token_limit > 0)
            return Math.min(100, ((record.token_count || 0) / limits.token_limit) * 100);
        if (limitMode === 'cost' && limits.cost_limit > 0)
            return Math.min(100, ((record.cost || 0) / limits.cost_limit) * 100);
        return 0;
    };

    const getBarColor = (util) => {
        if (util >= 100) return 'bg-red-500';
        if (util >= 80) return 'bg-amber-500';
        return 'bg-green-500';
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-[640px] max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">Usage History</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Spinner text="Loading..." /></div>
                    ) : records.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-12">No history records</p>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-gray-50">
                                <tr className="border-b border-gray-200">
                                    <th className="text-left px-4 py-2 font-medium text-gray-500">Window</th>
                                    <th className="text-right px-4 py-2 font-medium text-gray-500">Reqs</th>
                                    <th className="text-right px-4 py-2 font-medium text-gray-500">Tokens</th>
                                    <th className="text-right px-4 py-2 font-medium text-gray-500">Cost</th>
                                    <th className="px-4 py-2 font-medium text-gray-500 w-28">Usage</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {records.map((r, i) => {
                                    const util = getUtilization(r);
                                    return (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-gray-700">
                                                <span>{fmtWindow(r.window_start)}</span>
                                                <span className="text-gray-400 mx-1">-</span>
                                                <span>{fmtWindow(r.window_end)}</span>
                                            </td>
                                            <td className="px-4 py-2 text-right text-gray-700">{r.request_count}</td>
                                            <td className="px-4 py-2 text-right text-gray-700">{(r.token_count || 0).toLocaleString()}</td>
                                            <td className="px-4 py-2 text-right font-mono text-gray-700">${(r.cost || 0).toFixed(4)}</td>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-x-auto">
                                                        <div className={`h-full rounded-full ${getBarColor(util)}`} style={{ width: `${Math.min(util, 100)}%` }} />
                                                    </div>
                                                    <span className="text-[10px] text-gray-500 w-8 text-right shrink-0">{Math.round(util)}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

function UsageStatusCard({ usageStatus, userId }) {
    const [showHistory, setShowHistory] = useState(false);

    if (!usageStatus) return null;

    const { windows = [], is_allowed, tier, denied_by } = usageStatus;

    const WINDOW_LABELS = { weekly: 'Weekly', monthly: 'Monthly', daily: 'Daily', trial: 'Trial' };

    const getStatusLabel = () => {
        if (is_allowed === false) return { text: 'Exceeded', cls: 'text-red-600 bg-red-50' };
        const maxUtil = Math.max(0, ...windows.flatMap(w => Object.values(w.limits || {}).map(d => d.utilization)));
        if (maxUtil >= 80) return { text: 'Warning', cls: 'text-amber-600 bg-amber-50' };
        return { text: 'OK', cls: 'text-green-600 bg-green-50' };
    };

    const status = getStatusLabel();

    return (
        <>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700">Budget Status</h3>
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-400">{tier}</span>
                        <button
                            onClick={() => setShowHistory(true)}
                            className="flex items-center gap-1 px-1.5 py-0.5 text-[11px] text-gray-500 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                        >
                            History
                        </button>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${status.cls}`}>{status.text}</span>
                    </div>
                </div>
                <div className="space-y-3">
                    {windows.length > 0 ? windows.map((w, i) => {
                        const costDim = w.limits?.cost;
                        const reqDim = w.limits?.requests;
                        const maxUtil = Math.max(costDim?.utilization || 0, reqDim?.utilization || 0);
                        const barColor = maxUtil >= 100 ? 'bg-gray-400' : maxUtil >= 80 ? 'bg-amber-500' : 'bg-green-500';

                        return (
                            <div key={w.type || i}>
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-gray-500">{WINDOW_LABELS[w.type] || w.type}</span>
                                    <span className="font-medium text-gray-700">{Math.round(maxUtil)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                    <div className={`h-full transition-all ${barColor}`} style={{ width: `${Math.min(maxUtil, 100)}%` }} />
                                </div>
                                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                                    {costDim && <span>${costDim.used.toFixed(4)} / ${costDim.limit.toFixed(2)}</span>}
                                    {w.reset_time && <span>Reset: {w.reset_time}</span>}
                                    {w.days_remaining !== undefined && <span>{w.days_remaining}d left</span>}
                                </div>
                            </div>
                        );
                    }) : (
                        <p className="text-xs text-gray-400">No windows configured</p>
                    )}
                    {denied_by && (
                        <div className="text-xs text-red-500">
                            Denied: {denied_by.window} ({denied_by.limit_type})
                        </div>
                    )}
                </div>
            </div>
            {showHistory && <UsageHistoryPopup userId={userId} onClose={() => setShowHistory(false)} />}
        </>
    );
}

export default function UserOverviewTab({ userId }) {
    const [data, setData] = useState(null);
    const [usageStatus, setUsageStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadData();
    }, [userId]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [overview, usage] = await Promise.all([
                adminApi.getUserOverview(userId),
                adminApi.getUserUsageStatus(userId).catch(() => null),
            ]);
            setData(overview);
            setUsageStatus(usage);
        } catch (err) {
            console.error('Failed to load overview:', err);
            setError(err.message || 'Failed to load overview data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Spinner text="Loading overview..." center />;
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <p className="text-red-600">{error}</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center py-12 text-gray-400">
                <p>No data available</p>
            </div>
        );
    }

    const sessionsTrend = data.trends?.find(t => t.metric === 'Sessions');
    const costTrend = data.trends?.find(t => t.metric === 'Cost');
    const requestsTrend = data.trends?.find(t => t.metric === 'Requests');

    return (
        <div className="grid grid-cols-12 gap-6 h-full overflow-y-auto">
            {/* Left Column */}
            <div className="col-span-8 space-y-6">
                {/* Key Metrics */}
                {data.key_metrics && (
                    <Card title="Key Metrics (Last 30 Days)">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KeyMetricsCard
                                label="Avg Cost/Session"
                                value={fmtCost(data.key_metrics.avg_cost_per_session)}
                                trend={sessionsTrend}
                            />
                            <KeyMetricsCard
                                label="Avg Cost/Request"
                                value={fmtCost(data.key_metrics.avg_cost_per_request)}
                                trend={requestsTrend}
                            />
                            <KeyMetricsCard
                                label="Total Requests"
                                value={data.key_metrics.total_requests}
                                trend={requestsTrend}
                            />
                            <KeyMetricsCard
                                label="Most Used Model"
                                value={data.key_metrics.most_used_model || 'N/A'}
                                sublabel={data.model_breakdown?.[0] ? `${data.model_breakdown[0].percentage.toFixed(0)}% usage` : ''}
                            />
                        </div>
                    </Card>
                )}

                {/* Activity Trend */}
                <Card title="Activity Trend (30 days)">
                    <ActivityTrendChart activityTrend={data.activity_trend} />
                </Card>

                {/* Model Usage Breakdown */}
                {data.model_breakdown && data.model_breakdown.length > 0 && (
                    <Card title="Model Usage Breakdown">
                        <ModelBreakdownChart modelBreakdown={data.model_breakdown} />
                    </Card>
                )}

                {/* Recent Sessions */}
                <Card title="Recent Sessions">
                    {data.recent_sessions && data.recent_sessions.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-2 text-xs font-medium text-gray-500">Session</th>
                                        <th className="text-left py-2 text-xs font-medium text-gray-500">Created</th>
                                        <th className="text-right py-2 text-xs font-medium text-gray-500">Messages</th>
                                        <th className="text-right py-2 text-xs font-medium text-gray-500">Cost</th>
                                        <th className="text-right py-2 text-xs font-medium text-gray-500">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {data.recent_sessions.map((s) => (
                                        <tr key={s.session_id} className="hover:bg-gray-50">
                                            <td className="py-2 pr-4">
                                                <Link
                                                    to={`/admin/sessions?session_id=${s.session_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 hover:underline truncate max-w-xs block"
                                                >
                                                    {s.session_name}
                                                </Link>
                                            </td>
                                            <td className="py-2 text-xs text-gray-500">{fmtDate(s.created_at)}</td>
                                            <td className="py-2 text-right text-gray-700">{s.message_count}</td>
                                            <td className="py-2 text-right font-mono text-gray-700">{fmtCost(s.cost)}</td>
                                            <td className="py-2 text-right">
                                                <Link
                                                    to={`/admin/sessions?session_id=${s.session_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-blue-600 hover:text-blue-700"
                                                >
                                                    View
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-400 text-center py-4">No recent sessions</p>
                    )}
                </Card>
            </div>

            {/* Right Column: Smart Insights & Analytics */}
            <div className="col-span-4 space-y-4">
                {/* Usage Limit Status */}
                <UsageStatusCard usageStatus={usageStatus} userId={userId} />

                {/* Health Score */}
                {data.health_score && <HealthScoreCard healthScore={data.health_score} />}

                {/* Platform Comparison */}
                {data.platform_comparison && data.platform_comparison.length > 0 && (
                    <PlatformComparisonCard comparisons={data.platform_comparison} />
                )}

                {/* Peak Activity Hours */}
                {data.activity_pattern && <PeakActivityCard activityPattern={data.activity_pattern} />}

                {/* Storage Usage */}
                {data.resource_usage && <StorageUsageCard resourceUsage={data.resource_usage} />}

                {/* 7-Day Trend */}
                {data.seven_day_trend && <SevenDaySparkline sevenDayTrend={data.seven_day_trend} />}

                {/* Insights & Alerts */}
                {data.insights && data.insights.length > 0 ? (
                    data.insights.map((insight, idx) => (
                        <InsightCard
                            key={idx}
                            severity={insight.severity}
                            title={insight.title}
                            description={insight.description}
                        />
                    ))
                ) : (
                    <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4">
                        <div className="flex items-start gap-3">
                            <div className="shrink-0">
                                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h4 className="text-sm font-semibold text-gray-900">All Good</h4>
                                <p className="text-sm text-gray-600 mt-1">No insights or alerts for this user</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
